<div class="assets-container">
    <!-- Header Section -->
    <header class="assets-header">
        <div class="header-content">
            <div class="title-section">
                <h1 class="page-title">Assets</h1>
                <p class="page-subtitle">Manage your image assets</p>
            </div>
            <div class="header-controls">
                <div class="search-wrapper">
                    <mat-icon class="search-icon">search</mat-icon>
                    <input type="text" [formControl]="assetSearch" (input)="filtersQuery.set(assetSearch.value || '')"
                        placeholder="Search assets..." class="search-input" aria-label="Search assets"
                        [disabled]="isLoading()" />
                    @if (assetSearch.value) {
                    <button class="clear-btn" (click)="clearSearch()" matTooltip="Clear search"
                        aria-label="Clear search">
                        <mat-icon>close</mat-icon>
                    </button>
                    }
                </div>
                <button class="btn-upload" (click)="fileInput.click()" [disabled]="isUploading()">
                    <mat-icon>{{ isUploading() ? 'hourglass_empty' : 'cloud_upload' }}</mat-icon>
                    <span class="btn-text">{{ isUploading() ? 'Uploading...' : 'Upload' }}</span>
                </button>
            </div>
        </div>
        <input hidden type="file" name="files" #fileInput accept="image/*" (change)="onFileSelected($event)">
    </header>

    <!-- Loading State -->
    @if (isLoading()) {
    <div class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading assets...</p>
    </div>
    } @else {
    <!-- Main Content -->
    <main class="assets-content">
        @if (filteredAssets().length > 0) {
        <div class="table-container">
            <table class="assets-table">
                <thead>
                    <tr>
                        <th class="col-preview">Preview</th>
                        <th class="col-name">Name</th>
                        <th class="col-tags">Tags</th>
                        <th class="col-size">Size</th>
                        <th class="col-updated">Updated At</th>
                        <th class="col-updated-by">Updated By</th>
                        <th class="col-actions">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    @for (asset of filteredAssets(); track trackByAsset($index, asset)) {
                    <tr class="asset-row" (click)="viewAsset(asset)">
                        <td class="col-preview">
                            <div class="asset-thumbnail">
                                <img [src]="getCacheBustedUrl(asset)" [alt]="asset.altText || asset.displayName" loading="lazy">
                            </div>
                        </td>
                        <td class="col-name">
                            <div class="asset-name-cell">
                                <span class="asset-display-name">{{ asset.displayName }}</span>
                                @if (asset.description) {
                                <span class="asset-description">{{ asset.description }}</span>
                                }
                            </div>
                        </td>
                        <td class="col-tags">
                            @if (asset.tags && asset.tags.length > 0) {
                            <div class="tags-list">
                                @for (tag of asset.tags; track tag) {
                                <span class="tag-chip">{{ tag }}</span>
                                }
                            </div>
                            } @else {
                            <span class="no-tags">-</span>
                            }
                        </td>
                        <td class="col-size">
                            <span class="size-text">{{ formatFileSize(asset.size) }}</span>
                        </td>
                        <td class="col-updated">
                            @if (asset.updatedAt) {
                            <span class="date-text">{{ asset.updatedAt | dfnsFormatDistanceToNowStrict }} ago</span>
                            } @else if (asset.createdAt) {
                            <span class="date-text">{{ asset.createdAt | dfnsFormatDistanceToNowStrict }} ago</span>
                            } @else {
                            <span class="date-empty">-</span>
                            }
                        </td>
                        <td class="col-updated-by">
                            <span class="updated-by-text">{{ fbs.getUserNameFromId(asset.updatedBy || asset.createdBy)
                                || '-' }}</span>
                        </td>
                        <td class="col-actions">
                            <div class="action-buttons">
                                <button class="btn-action btn-copy" (click)="copyLink(asset); $event.stopPropagation()"
                                    matTooltip="Copy Link">
                                    <mat-icon>content_copy</mat-icon>
                                </button>
                                <button class="btn-action btn-download" (click)="downloadAsset(asset); $event.stopPropagation()"
                                    matTooltip="Download">
                                    <mat-icon>download</mat-icon>
                                </button>
                                <button class="btn-action btn-edit" (click)="editAssetMetadata(asset); $event.stopPropagation()"
                                    matTooltip="Edit Metadata">
                                    <mat-icon>edit</mat-icon>
                                </button>
                                @if (isAdmin) {
                                <button class="btn-action btn-update"
                                    (click)="replaceAsset(asset); $event.stopPropagation()" matTooltip="Replace Image">
                                    <mat-icon>sync</mat-icon>
                                </button>
                                <button class="btn-action btn-delete"
                                    (click)="deleteAsset(asset); $event.stopPropagation()" matTooltip="Delete">
                                    <mat-icon>delete_outline</mat-icon>
                                </button>
                                }
                            </div>
                        </td>
                    </tr>
                    }
                </tbody>
            </table>
        </div>
        <input hidden type="file" name="files" #singleFileInput accept="image/*" (change)="onReplaceFile($event)">

        <!-- Results Footer -->
        <footer class="results-footer">
            <div class="results-count">
                <mat-icon class="count-icon">folder_open</mat-icon>
                <span>Showing <strong>{{ filteredAssets().length }}</strong> of <strong>{{ allAssets().length
                        }}</strong> assets</span>
            </div>
        </footer>
        } @else {
        <!-- Empty State -->
        <div class="empty-state">
            <div class="empty-content">
                @if (assetSearch.value) {
                <div class="empty-icon-wrapper search">
                    <mat-icon class="empty-icon">search_off</mat-icon>
                </div>
                <h2 class="empty-title">No matching assets</h2>
                <p class="empty-description">We couldn't find any assets matching "<strong>{{ assetSearch.value
                        }}</strong>"</p>
                <button class="btn-empty" (click)="clearSearch()">
                    <mat-icon>clear</mat-icon>
                    <span>Clear Search</span>
                </button>
                } @else {
                <div class="empty-icon-wrapper">
                    <mat-icon class="empty-icon">image</mat-icon>
                </div>
                <h2 class="empty-title">No assets</h2>
                <p class="empty-description">Upload your first asset to get started</p>
                <button class="btn-empty" (click)="fileInput.click()">
                    <mat-icon>cloud_upload</mat-icon>
                    <span>Upload Asset</span>
                </button>
                }
            </div>
        </div>
        }
    </main>
    }
</div>
